<template>
  <div
    ref="root"
    class="tab tw:relative"
    :class="tabClass"
  >
    <BaseButton
      ref="button"
      v-bind="$attrs"
      :aria-disabled="disabled || undefined"
      :aria-selected="isActive"
      :class="
        twMerge(
          'button tw:relative tw:z-10 tw:w-full',
          isActive && 'active',
          buttonClass
        )
      "
      :data-testid="`tab-${name}`"
      :disabled="disabled"
      flat
      :icon="icon"
      :label="label"
      :new-design="newDesign"
      :new-icon="newIcon"
      :padding="padding"
      role="tab"
      square
      :tabindex="isRoving && !disabled ? 0 : -1"
      :to="to"
      @click="handleClick"
    >
      <slot />
    </BaseButton>

    <div
      v-show="!hideIndicator"
      :ref="tab.indicator"
      class="indicator"
      :class="{ active: isActive }"
    />
  </div>
</template>

<script lang="ts">
export default {
  name: "BaseTab",
};
</script>

<script setup lang="ts">
import { twMerge } from "tailwind-merge";
import {
  type ComponentPublicInstance,
  computed,
  inject,
  onMounted,
  onUnmounted,
  type Ref,
  ref,
  useTemplateRef,
} from "vue";
import { type RouteLocationRaw, useRouter } from "vue-router";

import BaseButton from "shared/components/base/BaseButton";
import { TabsSymbol } from "shared/components/base/symbols";
import { safePush } from "shared/helpers/routing";

export interface TabProps {
  buttonClass?: string;
  disabled?: boolean;
  hideIndicator?: boolean;
  icon?: string;
  indicatorColor?: string;
  label: string;
  name: string | number;
  newDesign?: boolean;
  newIcon?: boolean;
  padding?: string;
  to?: RouteLocationRaw;
  type?: "default" | "pill" | "toggle";
}

export interface Tab {
  disabled: () => boolean;
  focus: () => void;
  indicator: Ref<HTMLElement | undefined>;
  name: string | number;
}

const props = withDefaults(defineProps<TabProps>(), {
  buttonClass: "",
  icon: "",
  indicatorColor: "primary",
  padding: "md lg",
  to: undefined,
  type: "default",
});

const emit = defineEmits<{
  click: [];
}>();

const root = useTemplateRef<HTMLDivElement>("root");
const button = useTemplateRef<ComponentPublicInstance>("button");

const router = useRouter();
const push = safePush({ router });

const tabs = inject(TabsSymbol);

const isActive = computed<boolean>(() => props.name === tabs?.currentTab.value);

// Roving tabindex follows the focused tab so arrow keys move the tab-sequence
// entry point; it falls back to the selected tab when focus is outside the list.
const isRoving = computed<boolean>(
  () => props.name === (tabs?.focusedName.value ?? tabs?.currentTab.value)
);

const tab: Tab = {
  disabled: () => Boolean(props.disabled),
  focus: () => {
    const el = button.value?.$el;

    if (el instanceof HTMLElement) el.focus();
  },
  indicator: ref(),
  name: props.name,
};

const tabClass = computed(() => {
  const tabClassList = [props.indicatorColor];

  if (props.newDesign) {
    tabClassList.push(props.type);
  }

  if (isActive.value) {
    tabClassList.push("active");
  }

  return tabClassList;
});

const handleClick = (event: MouseEvent) => {
  const { name, to } = props;

  if (to) {
    event.preventDefault();
    push(to);
    tabs?.selectTab(name);
  } else {
    tabs?.selectTab(name);
  }

  emit("click");
};

onMounted(() => {
  tabs?.register(tab);
});

onUnmounted(() => {
  tabs?.unregister(tab);
});

defineExpose({
  el: root,
  name: tab.name,
});
</script>

<style scoped>
@reference "shared/css/tailwind.css";

.tab {
  --tab-indicator-color: transparent;

  .button:focus-visible {
    outline: none;
  }

  .button:focus-visible > :deep(span:not(.base-button__icon)) {
    outline: 2px solid currentcolor;
    outline-offset: 2px;
  }

  &.pill .button:focus-visible > :deep(span:not(.base-button__icon)),
  &.toggle .button:focus-visible > :deep(span:not(.base-button__icon)) {
    /* Widen the ring horizontally; negative margin keeps layout unchanged. */
    margin-inline: -4px;
    padding-inline: 4px;
    border-radius: 9999px;
  }

  .indicator {
    @apply tw:absolute tw:bottom-0 tw:left-0 tw:h-1 tw:w-full tw:origin-top-left tw:transition-colors;

    background: var(--tab-indicator-color);
  }

  &:hover {
    &:not(.active) {
      --tab-indicator-color: var(--s-color-denim-2);
    }
  }

  &.active {
    --tab-indicator-color: var(--s-tab-indicator);

    &.tv {
      --tab-indicator-color: var(--s-color-media-tv);
    }

    &.online {
      --tab-indicator-color: var(--s-color-media-online);
    }

    &.print {
      --tab-indicator-color: var(--s-color-media-print);
    }

    &.blue {
      --tab-indicator-color: var(--s-color-blue-1);
    }

    &.insights {
      --tab-indicator-color: var(--s-ai-secondary);
    }
  }

  &.pill {
    .button {
      @apply tw:border-none;

      --c-button-bg-default: transparent;
      --c-button-bg-hover: transparent;
      --c-button-bg-active: transparent;
    }

    .indicator {
      @apply tw:bottom-auto tw:top-0 tw:z-0 tw:h-full tw:rounded-full;
    }

    &.active {
      .button {
        --c-button-color: var(--s-button-color-primary-default);
      }

      &.blue {
        .button {
          --c-button-color: var(--s-color-blue-7);
          --c-button-color-hover: var(--s-color-blue-7);
        }
      }

      &.insights {
        .button {
          --c-button-color: var(--s-ai-secondary);
          --c-button-color-hover: var(--s-ai-secondary);
        }
      }
    }
  }

  &.toggle {
    .button {
      @apply tw:border-none;

      --c-button-bg-default: transparent;
      --c-button-bg-hover: transparent;
      --c-button-bg-active: transparent;
      --c-button-color: var(--s-color-denim-8);
    }

    .indicator {
      @apply tw:bottom-auto tw:top-0 tw:z-0 tw:h-full tw:rounded-full;

      --tab-indicator-color: transparent;
    }

    &.active {
      .button {
        --c-button-color: var(--s-button-color-secondary-default);
      }

      .indicator {
        --tab-indicator-color: white;
      }
    }
  }
}
</style>
